import { createContext, useContext, useMemo, useState } from "react";
import { Rfx } from "../types/rfx.types";
import { isRfxOnboardingType } from "../utils";

export interface RfxUploads {
	rfxBidder: any[];
	rfxProcurer: any[]; // you can replace `any` with your file type
}

interface RfxContextType {
	currentCompanyId: number | null;
	guestUserId: number | null;
	isOnboardingRFx: boolean;
	rfqId: number | null;
	rfxData: Rfx | null;
	saveHandler: (() => void) | null;
	setRFxData: React.Dispatch<React.SetStateAction<Rfx | null>>;

	uploadInProgresses: RfxUploads;
	// Functions / setters
	setCurrentCompanyId: (id: number | null) => void;
	setGuestUserId: (id: number | null) => void;
	setRFQId: (id: number | null) => void;
	setSaveHandler: (handler: (() => void) | null) => void;
	setUploadInProgresses: (uploads: RfxUploads) => void;
}

export const RfxContext = createContext<RfxContextType | null>(null);

export const RfxContextProvider = ({ children }) => {
	const [rfqId, setRFQId] = useState<number | null>(null);
	const [rfxData, setRFxData] = useState<Rfx | null>(null);
	const [guestUserId, setGuestUserId] = useState<number | null>(null);
	const [currentCompanyId, setCurrentCompanyId] = useState<number | null>(null);
	const [uploadInProgresses, setUploadInProgresses] = useState<RfxUploads | null>({
		rfxProcurer: [],
		rfxBidder: [],
	});
	const [saveHandler, setSaveHandler] = useState(null);

	const { isOnboardingRFx } = useMemo(() => {
		const isOnboardingRFx = isRfxOnboardingType(
			rfxData?.rfxType || rfxData?.type
		);

		return { isOnboardingRFx: isOnboardingRFx };
	}, [rfxData?.rfxType, rfxData?.type]);

	const onSettingRfxData = (rfxData: Rfx | null): void => {
		setRFxData(rfxData);
		setRFQId(rfxData?.id ?? rfqId);
	};

	return (
		<RfxContext.Provider
			value={{
				currentCompanyId,
				guestUserId,
				isOnboardingRFx: isOnboardingRFx ?? false,
				rfxData,
				rfqId,
				saveHandler,
				uploadInProgresses,

				// Functions / setters
				setCurrentCompanyId,
				setGuestUserId,
				setRFxData: onSettingRfxData,
				setRFQId,
				setSaveHandler,
				setUploadInProgresses,
			}}
		>
			{children}
		</RfxContext.Provider>
	);
};

export const useRfxContext = (): RfxContextType => {
	const context = useContext(RfxContext);
	if (!context) {
		throw new Error("useRfxContext must be used within RfxContextProvider");
	}
	return context;
};
